import { ScheduleMeeting } from "react-schedule-meeting";
import { enUS } from "date-fns/locale";
import { db } from "../firebase";
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore'; 
import { IoClose } from "react-icons/io5";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, useMemo, useCallback } from "react"; 
import { fromZonedTime } from 'date-fns-tz';
import { formatWarehouse, formatPseudo, warehouseMonthKey, warehouseNow, warehousePseudoDate, WAREHOUSE_TIMEZONE } from '../warehouseTime';
import {
  updateDates,
  removeSecondDate,
  removeFirstDate,
} from "../features/counter/counterSlice";
import moment from "moment";
import { DEFAULT_RESERVATION_POOLS, DEFAULT_TOTAL_DAILY_CAPACITY, generateDailyTimeSlots, reservationMatchesPool } from "../reservationPools";

const createSeededRng = (seed) => {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

export default function Scheduler(props) {
  // Odbieramy liste swiat i funkcje z PreAlert.js
  const { customHolidays, findAvailableDate, checkNumberOfPallets, cars, reservationPools = DEFAULT_RESERVATION_POOLS } = props;
  const siteName = process.env.REACT_APP_SITE_NAME || "Inbound appointment Slam Hoyerswerda";
  const siteEnv = (process.env.REACT_APP_SITE_ENV || "").toLowerCase();
  const isOschatzSite = siteEnv === "oschatz" || siteName.includes("Oschatz");
  const defaultLooseSlotsPerMonth = isOschatzSite ? 1 : 2;
  const dispatch = useDispatch();
  const firstDate = useSelector((state) => state.store.firstDate);
  const secondDate = useSelector((state) => state.store.secondDate);
  const type = useSelector((state) => state.store.type);
  const [fButtonActive, setFButtonActive] = useState(true);
  const [sButtonActive, setSButtonActive] = useState(false);
  const [dates, setDates] = useState([]); // Przechowuje wszystkie zajete daty
  const [allAlerts, setAllAlerts] = useState([]); // Przechowuje pelne dane alertow
  const [normalSlots, setNormalSlots] = useState(
    (isOschatzSite
      ? ["08:00", "09:20", "10:40", "12:00", "13:20", "14:40"]
      : generateDailyTimeSlots(DEFAULT_TOTAL_DAILY_CAPACITY)
    ).map((time) => {
      const [hour, minute] = time.split(':').map(Number);
      return { hour, minute };
    })
  );
  const [looseSlotsPerMonth, setLooseSlotsPerMonth] = useState(defaultLooseSlotsPerMonth);

  // Nasluchuj rezerwacji (dat) z bazy
  useEffect(() => {
    const alertsCollectionRef = collection(db, 'pre-alerts_MIGRATED', 'euoso0pRR5PZw2wBnjOW', 'alerts');
    const unsubscribe = onSnapshot(alertsCollectionRef, (querySnapshot) => {
      const alerts = querySnapshot.docs
        .map(doc => doc.data())
        .filter(alert => !alert.isCancel); // Nie licz anulowanych
      
      setAllAlerts(alerts); // Store full alert objects
      
      const newDates = alerts.map(alert =>
        formatWarehouse(alert.firstDate, 'yyyy-MM-dd HH:mm:ss')
      );
      setDates(newDates);
    });
    return () => unsubscribe();
  }, []);

  // Pobierz ustawienia slotów z bazy
  useEffect(() => {
    const loadSlotsConfig = async () => {
      try {
        const slotsConfigRef = doc(db, 'calendar', 'slotsConfig');
        const docSnap = await getDoc(slotsConfigRef);
        if (docSnap.exists()) {
          const config = docSnap.data();
          const configuredTimes = isOschatzSite
            ? config.normalSlots
            : generateDailyTimeSlots(config.totalDailyCapacity ?? DEFAULT_TOTAL_DAILY_CAPACITY);
          if (Array.isArray(configuredTimes)) {
            const slotsArray = configuredTimes.map(timeStr => {
              const [hour, minute] = timeStr.split(':').map(Number);
              return { hour, minute };
            });
            setNormalSlots(slotsArray);
          }
          if (config.looseSlotsPerMonth !== undefined) setLooseSlotsPerMonth(config.looseSlotsPerMonth);
        }
      } catch (error) {
        console.error("Error loading slots config:", error);
      }
    };
    loadSlotsConfig();
  }, [isOschatzSite]);

  // --- LOGIKA GENEROWANIA DOSTEPNYCH SLOTOW ---
  const availableTimeslots = useMemo(() => {
    const slots = [];

    // 6 "normalnych" slotow (rowne 80-minutowe odstepy)
    // Dni robocze: 1=Pon, 2=Wt, 3=Sr, 4=Czw, 5=Pt
    const WEEKDAYS = [1, 2, 3, 4, 5]; 

    // Oblicz date graniczna 24h
    let cutoffTime = moment(warehouseNow()).add(24, 'hours');
    while (
      cutoffTime.day() === 0 || 
      cutoffTime.day() === 6 || 
      (customHolidays && customHolidays.includes(cutoffTime.format('YYYY-MM-DD')))
    ) {
      cutoffTime.add(1, 'day').hour(8).minute(0).second(0);
    }

    // --- LOGIKA DLA 'trailer', 'swapbody', 'bus, van' ---
    if (type !== "loose") {
      let loopDate = cutoffTime.clone().startOf('day');
      for (let day = 0; day < 365; day++) { 
        const dateStr = loopDate.format('YYYY-MM-DD');
        const isWeekend = loopDate.day() === 0 || loopDate.day() === 6;
        const isCustomHoliday = customHolidays && customHolidays.includes(dateStr);

        // Sprawdzamy pojemnosc paletowa dla CALEGO DNIA
        const isDayNotFull = checkNumberOfPallets(loopDate.toDate());

        if (!isWeekend && !isCustomHoliday && isDayNotFull) {
          normalSlots.forEach(slot => {
            const startTime = loopDate.clone().hour(slot.hour).minute(slot.minute).second(0).toDate();
            if (startTime >= cutoffTime.toDate()) {
              slots.push({
                id: `slot-${day}-${slot.hour}-${slot.minute}`,
                startTime: startTime,
                endTime: moment(startTime).add(40, 'minutes').toDate(),
              });
            }
          });
        }
        loopDate.add(1, 'day');
      }
    } 
    // --- LOGIKA DLA 'loose' (limity na MIESIĄC) ---
    else { 
      const maxLooseDate = moment(warehouseNow()).add(2, 'months').endOf('day');
      const searchStartMonth = cutoffTime.clone().startOf('month');

      for (let monthOffset = 0; monthOffset < 12; monthOffset++) {
        const monthDate = searchStartMonth.clone().add(monthOffset, 'month');
        if (monthDate.isAfter(maxLooseDate, 'month')) break;
        
        // CRITICAL: Count existing loose reservations in this month
        // (month boundaries are compared in the warehouse timezone)
        
        // Debug: Log first few alerts to see data structure
        if (monthOffset === 0 && allAlerts.length > 0) {
          console.log(`[DEBUG] Sample alert:`, allAlerts[0]);
          console.log(`[DEBUG] Total alerts in DB: ${allAlerts.length}`);
        }
        
        const existingLooseInMonth = allAlerts.filter(alert => {
          if (!alert || !alert.type) return false;
          if (alert.type.toLowerCase() !== 'loose') return false;
          if (!alert.firstDate || !alert.firstDate.seconds) return false;

          const isInMonth = warehouseMonthKey(alert.firstDate) === monthDate.format('YYYY-MM');
          
          if (isInMonth) {
            console.log(`[DEBUG] Found loose in ${monthDate.format('YYYY-MM')}: ${alert.type}, date: ${formatWarehouse(alert.firstDate, 'yyyy-MM-dd')}`);
          }
          
          return isInMonth;
        });

        const selectedPools = isOschatzSite
          ? []
          : reservationPools.filter((pool) =>
              (cars || []).some((car) => (pool.codes || []).includes(String(car.receiving || "").trim().toUpperCase()))
            );
        const availableLooseSlots = selectedPools.length > 0
          ? Math.min(...selectedPools.map((pool) => {
              const used = existingLooseInMonth.filter((alert) => reservationMatchesPool(alert, pool)).length;
              return Number(pool.looseSlotsPerMonth || 0) - used;
            }))
          : looseSlotsPerMonth - existingLooseInMonth.length;
        
        // Skip this month if limit already reached
        if (availableLooseSlots <= 0) {
          console.log(`[SCHEDULER] Month ${monthDate.format('YYYY-MM')}: loose capacity reached - SKIPPING`);
          continue;
        }
        
        console.log(`[SCHEDULER] Month ${monthDate.format('YYYY-MM')}: ${availableLooseSlots} loose slots available`);
        
        const daysInMonth = monthDate.daysInMonth();
        const monthSeed = monthDate.year() * 100 + (monthDate.month() + 1);
        const rngSeed = monthSeed * 100 + (isOschatzSite ? 1 : 2);
        const rng = createSeededRng(rngSeed);
        const candidateSlots = [];

        for (let day = 1; day <= daysInMonth; day++) {
          for (const slot of normalSlots) {
            const slotTime = monthDate.clone()
              .date(day)
              .hour(slot.hour)
              .minute(slot.minute)
              .second(0);

            const dateStr = slotTime.format('YYYY-MM-DD');
            const isCustomHoliday = customHolidays && customHolidays.includes(dateStr);
            const isDayNotFull = checkNumberOfPallets(slotTime.toDate());
            const isWeekday = WEEKDAYS.includes(slotTime.isoWeekday());

            if (
              isWeekday &&
              slotTime.toDate() >= cutoffTime.toDate() &&
              slotTime.isSameOrBefore(maxLooseDate) &&
              !isCustomHoliday &&
              isDayNotFull
            ) {
              candidateSlots.push(slotTime.toDate());
            }
          }
        }

        for (let i = candidateSlots.length - 1; i > 0; i--) {
          const j = Math.floor(rng() * (i + 1));
          [candidateSlots[i], candidateSlots[j]] = [candidateSlots[j], candidateSlots[i]];
        }

        // Only generate remaining slots (limit - existing)
        const monthlySlots = candidateSlots
          .slice(0, availableLooseSlots)
          .map((startTime, index) => ({
            id: `slot-m${monthOffset}-${index + 1}`,
            startTime,
            endTime: moment(startTime).add(60, 'minutes').toDate(),
          }));

        monthlySlots.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        slots.push(...monthlySlots);
      }
    }

    return slots;
  }, [type, customHolidays, cars, checkNumberOfPallets, normalSlots, looseSlotsPerMonth, allAlerts, reservationPools, isOschatzSite]);

  // Filtruj sloty, ktore sa juz zajete
  const availableTimeSlotsLessUnavailableTimeSlots = useMemo(() => {
    // Konwertuj stringi (YYYY-MM-DD HH:mm:ss) na millisekund
    const bookedSlots = dates.map(dateStr => 
      moment(dateStr, "YYYY-MM-DD HH:mm:ss").toDate().getTime()
    );
    
    // Filtruj dostępne sloty - odrzuć zarezerwowane
    return availableTimeslots.filter(slot => {
      return !bookedSlots.includes(slot.startTime.getTime());
    });
  }, [availableTimeslots, dates]);

  useEffect(() => {
    // Ten kod jest prawdopodobnie niepotrzebny, ale na razie go zostawiamy
    if (cars.length > 1 && cars[1].receiving === "" && (cars[1].numberFBA === "") & (cars[1].type === "") && cars[1].palletsNumber === "" && cars[1].quantity === "") {
      dispatch(removeSecondDate());
    }
  }, [firstDate, secondDate, cars, dispatch]);

  const checkDates = (date) => {
    return dates.some((data) => data === date);
  };

  const update = async (date) => {
    const utcDate = fromZonedTime(date, WAREHOUSE_TIMEZONE);
    const localDateStr = formatPseudo(date, 'yyyy-MM-dd HH:mm:ss');
    
    // Logika limitow jest teraz wbudowana w 'availableTimeslots', wiec nie trzeba jej tu sprawdzac
    
    if (dates.includes(localDateStr)) {
      const availableDate = findAvailableDate(new Date(utcDate));
      alert(`This specific slot is already reserved. Please select another date.
        - Suggested: ${moment(availableDate).format('DD-MM-YYYY HH:mm:ss')}`);
      dispatch(updateDates({ date: "firstDate", value: '' }));
    } else {
      dispatch(updateDates({ date: "firstDate", value: new Date(utcDate).toISOString() }));
    }
  };

  const showDays = () => {
    const button = document.querySelector(".rsm-cancel-button");
    if (button) button.click();
  };

  useEffect(() => {
    const arrows = document.querySelectorAll(".rsm-arrow-button");
    const handleMonthChange = () => {
      showDays();
      dispatch(removeFirstDate());
    };
    arrows.forEach((arrow) => arrow.addEventListener("click", handleMonthChange));
    return () => {
      arrows.forEach((arrow) => arrow.removeEventListener("click", handleMonthChange));
    };
  }, [dispatch]);

  const changeFirstDate = () => {
    setFButtonActive(true);
    setSButtonActive(false);
  };

  const style = { outline: "1px solid #a6a6a6" };

  return (
    <>
      <div style={{ display: "flex", flexFlow: "row nowrap", alignItems: "center", justifyContent: "flex-end", width: "100%", height: "50px" }}>
        <IoClose onClick={props.handleOpen} style={{ width: "35px", height: "35px", color: "#FFD814", cursor: "pointer" }} />
      </div>
      <div>
        <button style={fButtonActive ? style : {}} onClick={changeFirstDate} className="date-button">
          Delivery date: {firstDate ? formatWarehouse(firstDate, 'dd.MM.yyyy HH:mm') : ""}
        </button>
      </div>
      <ScheduleMeeting
        borderRadius={8}
        primaryColor="#FFD814"
        eventDurationInMinutes={type === 'loose' ? 60 : 40}
        eventStartTimeSpreadInMinutes={0}
        availableTimeslots={availableTimeSlotsLessUnavailableTimeSlots}
        onStartTimeSelect={(e) => update(e.startTime)}
        onSelectedDayChange={showDays}
        startTimeListStyle="scroll-list"
        locale={enUS}
        format_startTimeFormatString="HH:mm"
        skipConfirmCheck={true}
        selectedStartTime={firstDate ? warehousePseudoDate(firstDate) : undefined}
      />
      <div style={{ display: "flex", justifyContent: "end", alignItems: "center", width: "100%", height: "100px", backgroundColor: "white", marginTop: "-20px" }}>
        <button
          type="button"
          onClick={props.handleOpen}
          style={{ marginRight: "1rem", height: "38px", textTransform: "capitalize" }}
          className="btn btn-warning"
        >
          Confirm
        </button>
      </div>
    </>
  );
}
